import store from '@/store/store';
import ApiService from '@/services/api.service';
import { TokenService, LanguageService } from '@/services/storage.service';
import SocketService from '@/services/socket.service';
import InstallationService from '@/services/installation.service';
import SceneService from '@/services/weather.service';
import { validateUser } from '@/models/user.model';
import { CONNECT } from '@/app.config';

const UserService = {
  /**
   * Obtiene los datos del usuario y sus instalaciones,
   * luego se conecta al socket y los guarda en el store
   *
   * @param {String} email - email del usuario
   * @param {String} password - Password del usuario
   * @throws {badParams} - Los datos enviados no son válidos
   * @throws {userNotExist} - Si el usuariono existe o si la contraseña es errónea
   * @throws {userNotConfirmed} - Si el usuario todavía no ha validado su cuenta
   * @throws {sessionExpired} - La sesión del usuario ha expirado
   * @throws {badResponse} - Los datos recibidos no son válidos
   * @throws {default} - Ocurrió otro error
   */
  // eslint-disable-next-line consistent-return
  async getUserData(email, password) {
    let user = null;

    if (email && password) {
      user = await this.login(email, password);
    } else {
      user = await this.isLogin();
    }

    if (!user) return null;

    const installations = await InstallationService.getInstallations();

    // Conecto los sockets
    await SocketService.connectUserSocket();

    if (installations) {
      // eslint-disable-next-line no-restricted-syntax
      for (const installationID in installations) {
        if ({}.hasOwnProperty.call(installations, installationID)) {
          SocketService.connectInstallationSocket(installationID);
          SceneService.getWeather(installationID, installations[installationID]);
        }
      }
    }

    // Salvo los datos del usuario y sus instalaciones en el store
    store.dispatch('userModule/saveUser', user);
    store.dispatch('globalModule/setLanguage', user.language);
    store.dispatch('unitsModule/saveInstallations', installations);
  },

  /**
   * Loguea al usuario, y almacena localmente el idioma del usuario,
   * el token de acceso y el de refresco
   *
   * @param {String} email - Email del usuario
   * @param {String} password - Contraseña del usuario
   * @return {User} - Datos del usuario parseados y validados
   * @throws {badParams} - Los datos enviados no son válidos
   * @throws {userNotExist} - Si el usuariono existe o si la contraseña es errónea
   * @throws {userNotConfirmed} - Si el usuario todavía no ha validado su cuenta
   * @throws {badResponse} - Los datos recibidos no son válidos
   * @throws {default} - Ocurrió otro error
   */
  async login(email, password) {
    // Request
    const resource = `auth/login/${CONNECT.SCOPE}`;
    const data = { email, password };
    const response = await ApiService.post(resource, data);

    // Check model
    const token = String(response.data.token);
    const refreshToken = String(response.data.refreshToken);

    /**
     * @type {User}
     */
    const user = validateUser(response.data);

    if (!user) return null;

    // Save local data and config api service
    TokenService.saveToken(token);
    TokenService.saveRefreshToken(refreshToken);
    LanguageService.saveLanguage(user.language);
    ApiService.setHeader(token);

    // Response
    return user;
  },

  /**
   * Comprueba si la sesión del usuario sigue abierta (token o refreshToken válidos).
   * Si la sesión sigue siendo válida devuelve los datos de usuario.
   * La petición de un nuevo token mediante el refreshToken
   * y la redirección al login en caso de que hayan caducado,
   * se realiza en el API Seervice.
   *
   * @return {User} - Datos de un usuario
   * @throws {badParams} - Los datos enviados no son válidos
   * @throws {networkError} - No hay conexión a internet
   * @throws {userNotExist} - si el usuario no existe
   * @throws {userNotConfirmed} - si el usuario todavía no ha validado su cuenta
   * @throws {sessionExpired} - La sesión del usuario ha expirado
   * @throws {sessionClosed} - La sesión ha sido cerrada desde otro dispostivo
   * @throws {badResponse} - Los datos recibidos no son válidos
   * @throws {default} - Ocurrió algún error
   */
  async isLogin() {
    // Request
    const resource = `users/isLoggedin/${CONNECT.SCOPE}`;
    const response = await ApiService.get(resource);

    // Check model
    const token = String(response.data.token);
    const refreshToken = String(response.data.refreshToken);

    /**
     * @type {User}
     */
    const user = validateUser(response.data);

    if (!user) return null;

    // Save local data and config api service
    TokenService.saveToken(token);
    TokenService.saveRefreshToken(refreshToken);
    LanguageService.saveLanguage(user.language);
    ApiService.setHeader(token);

    // Response
    return user;
  },

  /**
   * Trata de obtener un nuevo token válido mediante el refreshToken
   *
   * @return {String} token - Nuevo token
   * @throws {tokenNotFound} - No se encuentra el refreshToken en la Base de Datos
   * @throws {default} - Ocurrió algún error
   */
  async refreshToken() {
    // Request
    const refreshToken = TokenService.getRefreshToken();
    const resource = `auth/refreshToken/${refreshToken}/${CONNECT.SCOPE}`;
    const response = await ApiService.get(resource);

    // Response
    const { token, newRefreshToken } = response.data;

    // Save data
    TokenService.saveToken(token);
    TokenService.saveRefreshToken(newRefreshToken);
    ApiService.setHeader(token);

    return token;
  },

  /**
   * Envía un email al usuario para que pueda validar su cuenta
   * si el email se envía correctamente devuelve true.
   *
   * @return {Boolean} - Resultado de la petición
   * @throws {badParams} - Los datos enviados no son válidos
   * @throws {userNotExist} - El usuario no existe
   * @throws {userConfirmed} - El usuario ya había confirmado su cuenta
   * @throws {default} - Ocurrió algún error
   */
  async sendConfirmEmail(email) {
    // Request
    const resource = `auth/resendToken/${email}/${CONNECT.SCOPE}`;
    await ApiService.get(resource);

    return true;
  },

  /**
   * Confirma el registro de un usuario a través del token en el enlace
   * recibido vía email
   *
   * @param {String} token
   * @returns
   */
  async confirm(token) {
    const resource = `auth/confirmSignup/${token}`;
    await ApiService.get(resource);
    return true;
  },

  async resetPassword(token, data) {
    const resource = `auth/resetPass/${token}`;
    await ApiService.post(resource, data);
    return true;
  },

  async confirmSignup(token, data) {
    const resource = `auth/confirmSignup/${token}`;
    await ApiService.post(resource, data);
    return true;
  },

  async checkUserHash(token) {
    const resource = `auth/checkUserHash/${token}`;
    const response = await ApiService.get(resource);
    return response;
  },

  /**
   * Registra al usuario
   *
   * @param {Object} data - Datos del Usuario
   * @return {Boolean} - Resultado de registrar al usuario
   * @throws {badParams} - Los datos enviados no son válidos
   * @throws {userNotAvailable} - Si ya existe un usuario registrado con ese email
   * @throws {emailError} - Si el email no ha podido enviarse
   */
  async register(data) {
    // Request
    const resource = `auth/signup/${CONNECT.SCOPE}`;
    await ApiService.post(resource, data);

    // Response
    return true;
  },

  /**
   * Edita los datos de un usuario
   *
   * @param {Object} data - Datos del Usuario
   * @return {Boolean} - Resultado de editar al usuario
   * @throws {badParams} - Los datos enviados no son válidos
   */
  async editUser(data) {
    // Request
    if (!store.getters['globalModule/isDemo']) {
      const resource = `users/${CONNECT.SCOPE}`;
      await ApiService.patch(resource, data);
    }

    // Salvo los datos del usuario y sus instalaciones en el store
    store.dispatch('userModule/saveUser', data);

    // Response
    return true;
  },

  /**
   * Edita los ajustes de un usuario en un proyecto
   *
   * @param {Object} data - Datos del Usuario
   * @return {Boolean} - Resultado de registrar al usuario
   * @throws {badParams} - Los datos enviados no son válidos
   */
  async settings(data) {
    // Request
    if (!store.getters['globalModule/isDemo']) {
      const resource = `users/config/${CONNECT.SCOPE}`;
      await ApiService.patch(resource, data);
    }

    // Response
    return true;
  },

  /**
   * Elimina a un usuario
   *
   * @return {Boolean} - Resultado de eliminar al usuario
   * @throws {badParams} - Los datos enviados no son válidos
   */
  async deleteUser() {
    // Request
    const resource = 'users';
    await ApiService.delete(resource);
    this.cleanData();

    // Salvo los datos del usuario y sus instalaciones en el store
    store.dispatch('userModule/deleteUser');

    // Response
    return true;
  },

  /**
   * Envía un email para que el usuario modifique su contraseña
   *
   * @param {Object} email - Email del usuario
   * @param {String} password - User's password
   * @return {User, Installations}
   * @throws {badParams} - Los datos enviados no son válidos
   * @throws {userNotAvailable} - Si ya existe un usuario registrado con ese email
   * @throws {emailError} - Si el email no ha podido enviarse
   */
  async forgot(email) {
    // Request
    const resource = `auth/remember/${CONNECT.SCOPE}`;
    const data = { user_id: email };
    await ApiService.post(resource, data);

    // Response
    return true;
  },

  /**
   * Limpia los datos del usuario almacenados en el navegador
   */
  cleanData() {
    // Reset data
    TokenService.removeToken();
    TokenService.removeRefreshToken();
    LanguageService.removeLanguage();
    ApiService.removeHeader();
  },

  /**
   * Cierra sesión del usuario actual y limpia sus datos del navegador
   * Si nuestra sesión ha expirado o está cerrada, no es necesario realiz
   *
   * @throws {badParams} - si los argumentos del body no son válidos
   * @throws {userNotExist} - si el usuario no existe
   */
  async logout() {
    const resource = `users/logout/${CONNECT.SCOPE}`;
    await ApiService.get(resource);
    this.cleanData();
    SocketService.disconnectAllSocket();
  },
};

export default UserService;
